import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createSoilSightScenario } from "services";

import { useIsAuthenticated } from "utils";

import { CREATE_SOIL_SIGHT_SCENARIO, GET_SOIL_SIGHT_PROJECT, GET_SOIL_SIGHT_SCENARIO, LIST_SOIL_SIGHT_PROJECTS, LIST_SOIL_SIGHT_SCENARIOS } from "consts";

import { CreateSoilSightScenarioBody, SoilSightProjectResponse, SoilSightScenario, SoilSightScenarioDetail } from "models";

type MutateArgs = {
  soilSightProjectId: string;
  body: CreateSoilSightScenarioBody;
};

export const useCreateSoilSightScenario = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useIsAuthenticated();

  return useMutation({
    mutationKey: [CREATE_SOIL_SIGHT_SCENARIO],
    mutationFn: ({ soilSightProjectId, body }: MutateArgs) => {
      if (!isAuthenticated) return Promise.reject(new Error("Not Authorized"));
      return createSoilSightScenario(soilSightProjectId, body);
    },
    // Append to the list cache and seed the per-scenario detail cache instead of
    // invalidating. Other consumers that depend on a fresh refetch must call
    // invalidateQueries themselves. The endpoint only returns { success, scenarioId } (see
    // CreateSoilSightScenarioResponse) — never the full scenario, so the rest of the cached
    // row is filled from what we already know: the name we asked for, and "not a reference"
    // (the reference scenario is created with the project, never through this endpoint).
    onSuccess: (data, vars) => {
      if (!data?.scenarioId) return;
      const { scenarioId } = data;
      const scenarioName = vars.body.name;
      queryClient.setQueryData<SoilSightScenario[] | undefined>([LIST_SOIL_SIGHT_SCENARIOS, vars.soilSightProjectId], (old) =>
        old ? [...old, { id: scenarioId, name: scenarioName, isReference: false, createdAt: "", updatedAt: "" }] : old
      );
      queryClient.setQueryData<SoilSightScenarioDetail>([GET_SOIL_SIGHT_SCENARIO, scenarioId], {
        id: scenarioId,
        name: scenarioName,
        isReference: false,
        createdAt: "",
        updatedAt: "",
        cropYears: [],
      });
      const projectData = queryClient.getQueryData<SoilSightProjectResponse>([GET_SOIL_SIGHT_PROJECT, vars.soilSightProjectId]);
      queryClient.invalidateQueries({ queryKey: projectData?.farmId ? [LIST_SOIL_SIGHT_PROJECTS, projectData.farmId] : [LIST_SOIL_SIGHT_PROJECTS] });
    },
  });
};
